import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';

export interface SubscriptionRow {
  id: string;
  user_id: string;
  team_id: string | null;
  status: string;
  plan: string;
  start_date: string;
  end_date: string;
  is_trial: boolean;
  created_at: string;
}

export interface SubscriptionValidity {
  valid: boolean;
  status: string;
  daysRemaining: number;
}

export const getSubscription = async (userId: string): Promise<SubscriptionRow | null> => {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    logger.error('Subscription fetch error:', error);
    return null;
  }
  return data;
};

export const createTrialSubscription = async (userId: string, teamId?: string): Promise<SubscriptionRow | null> => {
  const now = new Date();
  const endDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  const { data, error } = await supabase
    .from('subscriptions')
    .upsert({
      user_id: userId,
      team_id: teamId || null,
      status: 'trial',
      plan: 'trial',
      start_date: now.toISOString(),
      end_date: endDate.toISOString(),
      is_trial: true,
    }, { onConflict: 'user_id' })
    .select()
    .maybeSingle();

  if (error) {
    logger.error('Trial subscription create error:', error);
    return null;
  }
  return data;
};

export const extendSubscription = async (subId: string, days: number): Promise<boolean> => {
  const { data: sub } = await supabase
    .from('subscriptions')
    .select('end_date')
    .eq('id', subId)
    .single();

  if (!sub) return false;

  const currentEnd = new Date(sub.end_date);
  const newEnd = new Date(currentEnd.getTime() + days * 24 * 60 * 60 * 1000);

  const { error } = await supabase
    .from('subscriptions')
    .update({
      end_date: newEnd.toISOString(),
      status: 'active',
      is_trial: false
    })
    .eq('id', subId);

  return !error;
};

export const suspendSubscription = async (subId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('subscriptions')
    .update({ status: 'suspended' })
    .eq('id', subId);

  return !error;
};

export const activateSubscription = async (subId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('subscriptions')
    .update({ status: 'active' })
    .eq('id', subId);

  return !error;
};

export const isSubscriptionValid = (sub: SubscriptionRow | null, role?: string): SubscriptionValidity => {
  // super_admin always valid
  if (role === 'super_admin') {
    return { valid: true, status: 'active', daysRemaining: 9999 };
  }

  if (!sub) {
    return { valid: false, status: 'none', daysRemaining: 0 };
  }

  if (sub.status === 'suspended') {
    return { valid: false, status: 'suspended', daysRemaining: 0 };
  }

  const now = new Date();
  const endDate = new Date(sub.end_date);
  const daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

  if (endDate > now) {
    return { valid: true, status: sub.status, daysRemaining };
  }

  return { valid: false, status: 'expired', daysRemaining: 0 };
};
